<template>
  <Transition name="fade">
    <div
      v-if="showSettings"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie-Einstellungen"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/40"
        @click="closeSettings"
      />

      <!-- Modal -->
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 class="text-xl font-bold text-dark-900">Cookie-Einstellungen</h2>
          <button
            @click="closeSettings"
            class="p-2 rounded-lg text-dark-400 hover:text-dark-900 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
            aria-label="Schließen"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-5">
          <!-- Notwendige Cookies -->
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1">
              <p class="font-semibold text-dark-900 mb-1">Notwendige Cookies</p>
              <p class="text-sm text-dark-500 leading-relaxed">
                Technisch erforderlich für die Grundfunktionalität der Website (z. B. Cookie-Zustimmung speichern). Können nicht deaktiviert werden.
              </p>
            </div>
            <!-- Always on toggle (disabled) -->
            <div class="shrink-0 mt-0.5">
              <div class="w-11 h-6 rounded-full bg-primary-500 relative cursor-not-allowed opacity-75">
                <div class="absolute top-0.5 right-0.5 bg-white rounded-full h-5 w-5 shadow-sm" />
              </div>
            </div>
          </div>

          <hr class="border-gray-100" />

          <!-- Funktionale Cookies -->
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1">
              <p class="font-semibold text-dark-900 mb-1">Funktionale Cookies</p>
              <p class="text-sm text-dark-500 leading-relaxed">
                Ermöglichen erweiterte Funktionen wie das Speichern von Formular-Zuständen und verbesserte Benutzerfreundlichkeit.
              </p>
            </div>
            <!-- Toggle -->
            <button
              class="shrink-0 mt-0.5 w-11 h-6 rounded-full relative transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1"
              :class="functionality ? 'bg-primary-500' : 'bg-gray-200'"
              :aria-checked="functionality"
              role="switch"
              aria-label="Funktionale Cookies"
              @click="functionality = !functionality"
            >
              <div
                class="absolute top-0.5 left-0.5 bg-white rounded-full h-5 w-5 shadow-sm transition-transform"
                :class="functionality ? 'translate-x-5' : ''"
              />
            </button>
          </div>

          <hr class="border-gray-100" />

          <!-- Analytik Cookies -->
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1">
              <p class="font-semibold text-dark-900 mb-1">Analytik Cookies</p>
              <p class="text-sm text-dark-500 leading-relaxed">
                Helfen zu verstehen, wie Besucher die Website nutzen. Aktuell nicht aktiv, aber für zukünftige Analysen vorbereitet.
              </p>
            </div>
            <!-- Toggle -->
            <button
              class="shrink-0 mt-0.5 w-11 h-6 rounded-full relative transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1"
              :class="analytics ? 'bg-primary-500' : 'bg-gray-200'"
              :aria-checked="analytics"
              role="switch"
              aria-label="Analytik Cookies"
              @click="analytics = !analytics"
            >
              <div
                class="absolute top-0.5 left-0.5 bg-white rounded-full h-5 w-5 shadow-sm transition-transform"
                :class="analytics ? 'translate-x-5' : ''"
              />
            </button>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="p-6 pt-2 flex flex-col sm:flex-row gap-3">
          <button
            @click="acceptNecessary"
            class="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-lg text-dark-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            Nur notwendige
          </button>
          <button
            @click="saveConsent(functionality, analytics)"
            class="flex-1 px-4 py-2.5 text-sm border border-primary-500 text-primary-500 rounded-lg hover:bg-primary-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            Auswahl speichern
          </button>
          <button
            @click="acceptAll"
            class="flex-1 px-4 py-2.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
const { showSettings, consentData, acceptAll, acceptNecessary, saveConsent, closeSettings } = useCookieConsent()

const functionality = ref(consentData.value?.functionality ?? false)
const analytics = ref(consentData.value?.analytics ?? false)

// Sync toggles when modal opens
watch(showSettings, (open) => {
  if (open) {
    functionality.value = consentData.value?.functionality ?? false
    analytics.value = consentData.value?.analytics ?? false
  }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
